import React, { memo, useCallback, useMemo } from 'react';
import { Avatar, Icon, Typography } from '@ann/web-components';
import { ASSIGNEE_TYPES, PERMISSION_TYPES, SHARE_MODAL_COPY } from '../../../../constants';
import { getAvatarSrc, getInitials } from '../../../../utils/avatarHelpers';
import { normalizeShareAssignment } from '../../../../utils/permissionsHelpers';
import PermissionsDropdown from '../PermissionsDropdown';
import {
  AssigneeActions,
  AssigneeAvatarWrap,
  AssigneeMeta,
  AssigneeNameInline,
  AssigneeRow,
  RemoveAssigneeButton,
} from '../../ShareDashboardModal.styled';

const VARIANT_EXISTING = 'existing';

const AssigneeRowItem = ({ item, variant, onPermissionChange, onRemove }) => {
  const isUser = item?.type === ASSIGNEE_TYPES.USER;

  const handlePermissionChange = useCallback(
    (payload) => onPermissionChange(item.id, payload),
    [item.id, onPermissionChange]
  );

  const handleRemove = useCallback(
    () => onRemove(item.id),
    [item.id, onRemove]
  );

  const displayText = useMemo(() => {
    if (variant === VARIANT_EXISTING) {
      return isUser
        ? `${item?.name || 'Unknown user'} ${item?.email || ''}`.trim()
        : `${item?.name || 'Unknown department'} · Department`;
    }
    const roleText = isUser
      ? SHARE_MODAL_COPY.tooltips.userRole(item.role)
      : `${item.name} · Department`;
    return isUser ? `${item.name} · ${roleText}` : roleText;
  }, [isUser, item?.email, item.name, item.role, variant]);

  const permissions = useMemo(() => {
    if (variant === VARIANT_EXISTING) {
      const normalized = normalizeShareAssignment(item);
      return {
        permissionLevel: normalized.permissionLevel,
        permissionModes: normalized.permissionModes,
        editPermissions: normalized.editPermissions,
        viewPermissions: normalized.viewPermissions,
      };
    }
    return {
      permissionLevel: item.permissionLevel || PERMISSION_TYPES.VIEW,
      permissionModes: item.permissionModes,
      editPermissions: item.editPermissions || [],
      viewPermissions: item.viewPermissions,
    };
  }, [item, variant]);

  return (
    <AssigneeRow>
      <AssigneeMeta>
        <AssigneeAvatarWrap>
          {isUser ? (
            <Avatar
              width={24}
              height={24}
              alt=""
              label={getInitials(item.name)}
              src={getAvatarSrc(item.name, item.id)}
            />
          ) : (
            <Icon
              name="work"
              size="custom"
              width="20px"
              height="20px"
              color="primary-alt"
            />
          )}
        </AssigneeAvatarWrap>
        <AssigneeNameInline>
          <Typography category="body" size="medium" weight="medium" color="body" titleOnHover>
            {displayText}
          </Typography>
        </AssigneeNameInline>
      </AssigneeMeta>
      <AssigneeActions>
        <PermissionsDropdown
          permissionLevel={permissions.permissionLevel}
          permissionModes={permissions.permissionModes}
          editPermissions={permissions.editPermissions}
          viewPermissions={permissions.viewPermissions}
          onChange={handlePermissionChange}
        />
        <RemoveAssigneeButton
          type="button"
          aria-label={SHARE_MODAL_COPY.assignedList.removeAssignee}
          onClick={handleRemove}
        >
          <Icon name="close" size="small" color="neutral" />
        </RemoveAssigneeButton>
      </AssigneeActions>
    </AssigneeRow>
  );
};

export default memo(AssigneeRowItem);
